import { bandVerdict, currentReadings, dailySeries, monthOf, targetFor, type Reading, type Target, type Verdict } from '@gk/contracts'
import type { FeatureCollectionOf } from './geo.ts'
import type { JobAreaRow, JobRow, JobState } from './plan.ts'

/** M16-E, "today's work on the map" (4; docs/M16.md E): no table, no command, pure from the device's own copy (the mirrored `ops.job` and `ops.job_area`, the finishes'
 *  coverage, the notebook), so the map colours the course and its area popup lists the day's jobs with no network. The rules, in the order the screen applies them:
 *  - a job is the day's when it is planned on that club day and not cancelled; it is on an area when a live `ops.job_area` row names it (a removed one does not);
 *  - an area's state is what remains to be done on it, from all of the day's jobs on it: work under way (in progress or paused) first, then work still planned,
 *    then work done in part, then work done; an area covered in FULL by the finish of a job that ended in part counts that job as done there (the union of full
 *    coverage over the job's finishes: what the server derives the job's state from, M8);
 *  - the popup lists the jobs on the area and on the areas under it (a hole's popup holds its green's and its tee's), in the board's order. */
export const WORK_STATES = ['planned', 'in_progress', 'partially_done', 'done'] as const
export type AreaWorkState = (typeof WORK_STATES)[number]

export type DayJob = Pick<JobRow, 'id' | 'state' | 'scheduled_local_date' | 'sequence_group' | 'scheduled_start_local'> & { title_text?: string }
type JobAreaLink = Pick<JobAreaRow, 'job_id' | 'area_id' | 'removed_by_command_id'>

/** The day's live jobs per area (cancelled ones and removed areas left out), each list in the board's order: first jobs first, then by start time, then by title. */
export function jobsByArea<J extends DayJob>(jobs: readonly J[], jobAreas: readonly JobAreaLink[], day: string): Map<string, J[]> {
  const byId = new Map(jobs.filter((j) => j.scheduled_local_date === day && j.state !== 'cancelled').map((j) => [j.id, j]))
  const out = new Map<string, J[]>()
  for (const l of jobAreas) {
    if (l.removed_by_command_id) continue
    const j = byId.get(l.job_id)
    if (!j) continue
    const list = out.get(l.area_id) ?? []
    if (!list.includes(j)) list.push(j)
    out.set(l.area_id, list)
  }
  for (const list of out.values()) list.sort(boardOrder)
  return out
}
const boardOrder = (a: DayJob, b: DayJob): number =>
  a.sequence_group - b.sequence_group || (a.scheduled_start_local ?? '99').localeCompare(b.scheduled_start_local ?? '99') || (a.title_text ?? '').localeCompare(b.title_text ?? '') || a.id.localeCompare(b.id)

/** `job|area` for every area a job's finishes covered in full (the union over its finishes, as the server judges the job). */
export function fullCoverage(completions: ReadonlyArray<{ id: string; job_id: string }>, coverage: ReadonlyArray<{ completion_id: string; area_id: string; coverage: 'full' | 'partial' }>): Set<string> {
  const jobOf = new Map(completions.map((c) => [c.id, c.job_id]))
  const out = new Set<string>()
  for (const c of coverage) { const job = jobOf.get(c.completion_id); if (job && c.coverage === 'full') out.add(`${job}|${c.area_id}`) }
  return out
}

const RANK: Record<AreaWorkState, number> = { in_progress: 0, planned: 1, partially_done: 2, done: 3 }
/** One job's state on one area: paused is work under way; a job done in part is done on an area it covered in full. */
export function jobStateOn(job: Pick<JobRow, 'id' | 'state'>, areaId: string, covered: ReadonlySet<string> = new Set()): AreaWorkState | null {
  const s: JobState = job.state
  if (s === 'cancelled') return null
  if (s === 'in_progress' || s === 'paused') return 'in_progress'
  if (s === 'partially_done') return covered.has(`${job.id}|${areaId}`) ? 'done' : 'partially_done'
  return s
}
/** What remains to be done on an area from the day's jobs on it (null with none): under way, then planned, then done in part, then done. */
export function areaWorkState(jobs: ReadonlyArray<Pick<JobRow, 'id' | 'state'>>, areaId: string, covered: ReadonlySet<string> = new Set()): AreaWorkState | null {
  let best: AreaWorkState | null = null
  for (const j of jobs) {
    const s = jobStateOn(j, areaId, covered)
    if (s && (best === null || RANK[s] < RANK[best])) best = s
  }
  return best
}

/** The areas of the day as a GeoJSON collection for the map's work layer: the shape in force (the map's own area collection) with the state, nothing for an
 *  area with no job that day or no shape. */
export function workFeatures(areas: FeatureCollectionOf, byArea: ReadonlyMap<string, ReadonlyArray<Pick<JobRow, 'id' | 'state'>>>, covered: ReadonlySet<string> = new Set()): FeatureCollectionOf {
  const features: FeatureCollectionOf['features'] = []
  for (const f of areas.features) {
    const jobs = byArea.get(f.id)
    const state = jobs ? areaWorkState(jobs, f.id, covered) : null
    if (state) features.push({ type: 'Feature', id: f.id, geometry: f.geometry, properties: { id: f.id, state, jobs: jobs!.length, area_m2: f.properties.area_m2 } })
  }
  return { type: 'FeatureCollection', features }
}
/** How many areas of the day are in each state (the legend's counts). */
export function workCounts(work: FeatureCollectionOf): Record<AreaWorkState, number> {
  const out: Record<AreaWorkState, number> = { planned: 0, in_progress: 0, partially_done: 0, done: 0 }
  for (const f of work.features) out[f.properties.state as AreaWorkState]++
  return out
}

/** The area and every area under it (cycle-safe), for the popup's list. */
export function withDescendants(areaId: string, areas: ReadonlyArray<{ id: string; parent_id: string | null }>): string[] {
  const kids = new Map<string, string[]>()
  for (const a of areas) if (a.parent_id) kids.set(a.parent_id, [...(kids.get(a.parent_id) ?? []), a.id])
  const out: string[] = []; const seen = new Set<string>(); const stack = [areaId]
  while (stack.length) { const id = stack.pop()!; if (seen.has(id)) continue; seen.add(id); out.push(id); stack.push(...(kids.get(id) ?? [])) }
  return out
}
/** The day's jobs on these areas, each once, in the board's order. */
export function jobsOn<J extends DayJob>(byArea: ReadonlyMap<string, readonly J[]>, areaIds: readonly string[]): J[] {
  const seen = new Set<string>(); const out: J[] = []
  for (const a of areaIds) for (const j of byArea.get(a) ?? []) if (!seen.has(j.id)) { seen.add(j.id); out.push(j) }
  return out.sort(boardOrder)
}

/** The popup's readings: per measurement type taken on the area, the latest day's mean (corrections replace what they correct) judged against the club's target of
 *  that month for the area's type (`bandVerdict`; no target, no verdict: nothing is called a standard), the types in the notebook's order of their latest day. */
export interface LatestReading { type: string; date: string; mean: number; n: number; target: Target | undefined; verdict: Verdict | null }
export function latestReadings(readings: readonly Reading[], targets: readonly Target[], areaId: string, areaType: string | null): LatestReading[] {
  const byType = new Map<string, Reading[]>()
  for (const r of currentReadings(readings.filter((x) => x.area_id === areaId))) byType.set(r.measurement_type, [...(byType.get(r.measurement_type) ?? []), r])
  const out: LatestReading[] = []
  for (const [type, list] of byType) {
    const last = dailySeries(list).at(-1)
    if (!last) continue
    const target = targetFor(targets, type, areaType, monthOf(last.date))
    out.push({ type, date: last.date, mean: last.mean, n: last.n, target, verdict: bandVerdict(last.mean, target) })
  }
  return out.sort((a, b) => b.date.localeCompare(a.date) || a.type.localeCompare(b.type))
}
